// Mock data for the Comparison screen (route `/comparison`).
// Follows the pattern of `src/data/mockPerformanceDividend.ts`.
// Confirmed via Figma MCP: all 10 card nodes share the same base values,
// so we vary only `code + name` per card while keeping chart/average values
// identical across cards.

import type {
  ComparisonAverageScore,
  ComparisonCardData,
  ComparisonData,
  ComparisonRatingBucket,
} from '@app-types/comparison';

// ---- Helpers --------------------------------------------------------
const buildRatings = (): ComparisonRatingBucket[] => [
  { key: 'veryBullish', label: '強気', value: 6, color: '#e60000' },
  { key: 'bullish', label: 'やや強気', value: 3, color: '#e28d8d' },
  { key: 'neutral', label: '中立', value: 1, color: '#acacac' },
  { key: 'bearish', label: 'やや弱気', value: 5, color: '#7d9eff' },
  { key: 'veryBearish', label: '弱気', value: 8, color: '#3366ff' },
];

const buildAverageScore = (): ComparisonAverageScore => ({
  current: '3.8',
  oneMonth: '3.8',
  threeMonths: '3.8',
  sixMonths: '3.8',
  nineMonths: '3.8',
  twelveMonths: '3.8',
});

// ---- Card seed data (nodeId, code, name) ---------------------------
type CardSeed = readonly [nodeId: string, code: string, name: string];

const cardSeeds: readonly CardSeed[] = [
  ['3185:9467', '8306', '三菱UFJ FG'],
  ['3185:9559', '8316', '三井住友FG'],
  ['3188:10821', '8411', 'みずほFG'],
  ['3188:10822', '8355', '静岡フィナンシャル'],
  ['3188:10940', '8331', '千葉銀行'],
  ['3188:10941', '8354', 'ふくおかFG'],
  ['3188:11059', '8341', '七十七銀行'],
  ['3188:11060', '8377', 'ほくほくFG'],
  ['3188:11178', '8358', 'スルガ銀行'],
  ['3188:11179', '8309', '三井住友トラストHD'],
];

// Figma specifies 10 cards (one page). Pagination needs more rows, so the
// seeds are cycled into 3 pages. Rows past the first page reuse the design
// node ids and get a visibly distinct code/name suffix so switching pages is
// verifiable; they are synthetic filler, not real instruments.
const CARD_PAGE_SIZE = 10;
const CARD_PAGE_COUNT = 3;

const cards: ComparisonCardData[] = Array.from(
  { length: CARD_PAGE_SIZE * CARD_PAGE_COUNT },
  (_, index): ComparisonCardData => {
    const [nodeId, code, name] = cardSeeds[index % cardSeeds.length];
    const cycle = Math.floor(index / cardSeeds.length);

    return {
      key: `card-${index + 1}`,
      nodeId,
      code: cycle === 0 ? code : String(Number(code) + cycle * 100),
      name: cycle === 0 ? name : `${name}${cycle + 1}`,
      price: '2,796.5',
      change: '+35.5 (+1.29%)',
      changeTone: 'up',
      ratings: buildRatings(),
      averageScore: buildAverageScore(),
    };
  },
);

// ---- Assembled screen data -----------------------------------------
export const mockComparison: ComparisonData = {
  tabs: [
    { key: 'rating', label: 'コンセンサスレーティング', active: true },
    { key: 'revenue', label: '売上高' },
    { key: 'ordinaryProfit', label: '経常利益' },
    { key: 'netIncome', label: '当期利益' },
  ],
  updatedAt: '2026/03/18 更新',
  cards,
  // Fallback only: ComparisonPage drives pagination through currentPage /
  // totalPages props. Kept so the component's static mode stays usable.
  pagination: {
    items: ['«', '1', '2', '3', '»'],
    selected: '1',
  },
};

export default mockComparison;
